import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  useCustomerFetch, Card, SectionLabel, StatusBadge, Sheet, EmptyState, SmileyRating,
  TICKET_STATUS_TONE, TICKET_STATUS_KEY,
} from "../../components/customer-app/ui";
import { useLanguage } from "../../contexts/LanguageContext";
import CustomerTicketPhotoGallery from "../../components/customer-app/CustomerTicketPhotoGallery";
import { ClipboardList, User, Users, CheckCircle2, Circle, Loader2, ChevronLeft, Route, PartyPopper, Hash, XCircle } from "lucide-react";

const TERMINAL_STATUSES = ["completed", "closed", "cancelled", "dropped"];

function isActive(t) { return ["open", "in_progress", "on_hold", "resolution_support_required"].includes(t.status); }
function isResolved(t) { return TERMINAL_STATUSES.includes(t.status); }
function canCancel(t) { return isActive(t) && t.type !== "new_installation"; }

export default function CustomerTickets() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const { data: tickets, loading, reload } = useCustomerFetch("/customer-app/tickets");
  const FILTERS = [
    { key: "all", label: t("tickets.filterAll") },
    { key: "active", label: t("tickets.filterActive") },
    { key: "wip", label: t("tickets.wipInstall") },
    { key: "resolved", label: t("tickets.filterResolved") },
  ];
  const [filter, setFilter] = useState("all");
  const [detailId, setDetailId] = useState(params.get("open") || null);
  const { data: detail, reload: reloadDetail } = useCustomerFetch(detailId ? `/customer-app/tickets/${detailId}` : null, [detailId]);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Deep-linked from Home ("Recent Requests") via ?open=<ticket_id> — open straight
  // into the detail sheet instead of making the customer tap the row again.
  useEffect(() => {
    const openId = params.get("open");
    if (openId) setDetailId(openId);
  }, [params]);

  // Live status — poll the open ticket every 8s so status changes made by the
  // technician/service team show up without the customer needing to reopen it.
  useEffect(() => {
    if (!detailId) return;
    const iv = setInterval(reloadDetail, 8000);
    return () => clearInterval(iv);
  }, [detailId, reloadDetail]);

  function closeDetail() {
    setDetailId(null);
    if (params.get("open")) {
      params.delete("open");
      setParams(params, { replace: true });
    }
  }

  const filtered = (tickets || []).filter((t) => {
    if (filter === "active") return isActive(t);
    if (filter === "wip") return t.type === "new_installation";
    if (filter === "resolved") return isResolved(t);
    return true;
  });

  async function submitFeedback() {
    if (!rating) { toast.error("Pick a smiley rating first"); return; }
    setSubmittingFeedback(true);
    try {
      await api.post(`/customer-app/tickets/${detailId}/feedback`, { rating, comment: comment || undefined });
      toast.success("Thanks for your feedback!");
      reloadDetail();
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmittingFeedback(false); }
  }

  async function cancelTicket() {
    if (!window.confirm(t("tickets.cancelConfirmBody"))) return;
    setCancelling(true);
    try {
      await api.put(`/customer-app/tickets/${detailId}/cancel`);
      toast.success(t("tickets.cancelledToast"));
      reloadDetail();
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setCancelling(false); }
  }

  return (
    <div className="space-y-3 pb-4" data-testid="customer-tickets">
      <button onClick={() => nav(-1)} data-testid="tickets-back-btn" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-1">
        <ChevronLeft className="w-3.5 h-3.5" /> {t("common.back")}
      </button>
      <SectionLabel>{t("tickets.myRequests")}</SectionLabel>


      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`ticket-filter-${f.key}`}
            className={"flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors " +
              (filter === f.key ? "bg-[#0D4721] text-white" : "bg-white text-slate-500 border border-slate-200")}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />}
      {!loading && filtered.length === 0 && (
        <EmptyState icon={ClipboardList} title={t("tickets.noRequests")} subtitle="Anything you raise, or that our team logs for you, will show up in this tab." />
      )}

      <div className="space-y-2">
        {filtered.map((tk) => (
          <button key={tk.id} onClick={() => { setDetailId(tk.id); setRating(null); setComment(""); }} data-testid={`ticket-card-${tk.ticket_number}`} className="w-full text-left">
            <Card className="p-3.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-800 truncate">{tk.title}</div>
                  <div className="text-[11px] font-mono text-slate-400 mt-0.5">{tk.ticket_number}</div>
                </div>
                <StatusBadge tone={TICKET_STATUS_TONE[tk.status]}>{t("tickets.status" + TICKET_STATUS_KEY[tk.status])}</StatusBadge>
              </div>
              <div className="flex items-center gap-1.5 mt-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {tk.raised_by === "customer_app" ? <><User className="w-3 h-3" /> {t("tickets.raisedByYou")}</> : <><Users className="w-3 h-3" /> {t("tickets.raisedByTeam")}</>}
                <span>· {new Date(tk.created_at).toLocaleDateString()}</span>
                {tk.csat_rating && <span className="ml-auto text-amber-500">Rated {tk.csat_rating}★</span>}
              </div>
              {tk.opportunity_summary && (
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
                  <span className="font-mono text-slate-400">{tk.opportunity_summary.code}</span>
                  <span>· Phase {tk.opportunity_summary.current_phase}/5</span>
                  {tk.installed_base_ref && (
                    <span className="ml-auto inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <Hash className="w-2.5 h-2.5" /> {tk.installed_base_ref.ib_number}
                    </span>
                  )}
                </div>
              )}
            </Card>
          </button>
        ))}
      </div>

      <Sheet open={!!detailId} onClose={closeDetail} title={detail?.ticket_number || "Request"} testid="ticket-detail-sheet">
        {!detail ? (
          <div className="py-8 text-center text-sm text-slate-400">{t("common.loading")}</div>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between">
                <div className="text-base font-bold text-slate-800">{detail.title}</div>
                <StatusBadge tone={TICKET_STATUS_TONE[detail.status]}>{t("tickets.status" + TICKET_STATUS_KEY[detail.status])}</StatusBadge>
              </div>
              {detail.description && <p className="text-sm text-slate-600 mt-1.5">{detail.description}</p>}
            </div>

            {canCancel(detail) && (
              <button
                onClick={cancelTicket}
                disabled={cancelling}
                data-testid="ticket-cancel-request-btn"
                className="w-full flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 py-3 rounded-xl text-sm font-bold hover:bg-red-50 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" /> {cancelling ? t("common.loading") : t("tickets.cancelRequest")}
              </button>
            )}

            {detail.opportunity_summary && (
              <Card className="p-3.5 bg-slate-50/70" data-testid="ticket-opportunity-summary">
                <SectionLabel className="mb-1.5">Linked Project</SectionLabel>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">{detail.opportunity_summary.name}</div>
                    <div className="text-[11px] font-mono text-slate-400 mt-0.5">{detail.opportunity_summary.code} · Phase {detail.opportunity_summary.current_phase}/5</div>
                  </div>
                  <Link to={`/app/track/${detail.opportunity_summary.id}`} data-testid="ticket-track-progress-link"
                    className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#0D4721] bg-white border border-slate-200 rounded-full px-2.5 py-1.5">
                    <Route className="w-3 h-3" /> Track
                  </Link>
                </div>
              </Card>
            )}

            {detail.installed_base_ref && (
              <div className="rounded-2xl p-3.5 bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-200 flex items-center gap-3" data-testid="ticket-installation-complete-banner">
                <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <PartyPopper className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-extrabold text-emerald-800">Installation Completed</div>
                  <Link to={`/app/installations?open=${detail.installed_base_ref.id}`} data-testid="ticket-installation-id-link"
                    className="text-[11px] font-mono font-bold text-emerald-700 underline">
                    Installation ID: {detail.installed_base_ref.ib_number}
                  </Link>
                </div>
              </div>
            )}

            {detail.activities?.length > 0 && (
              <div>
                <SectionLabel className="mb-3">{t("tickets.timeline")}</SectionLabel>
                <div className="relative pl-9">
                  <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-200" />
                  {detail.activities.map((a, i) => (
                    <div key={a.id} className="relative mb-4 last:mb-0">
                      <div className={"absolute -left-9 w-8 h-8 rounded-full flex items-center justify-center text-xs border-2 border-white shadow-sm z-10 " +
                        (a.status === "completed" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400 border-slate-300")}>
                        {a.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3.5 h-3.5" />}
                      </div>
                      <div className="text-sm font-semibold text-slate-800">{a.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                        {a.completed_at ? new Date(a.completed_at).toLocaleString() : t("tickets.pending")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.resolution_notes && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <SectionLabel className="mb-1 text-emerald-700">{t("tickets.resolutionSummary")}</SectionLabel>
                <div className="text-sm text-emerald-900">{detail.resolution_notes}</div>
                {detail.closed_at && <div className="text-[11px] font-mono text-emerald-600 mt-1">{t("tickets.statusClosed")} {new Date(detail.closed_at).toLocaleString()}</div>}
              </div>
            )}

            <CustomerTicketPhotoGallery ticketId={detail.id} />

            {["completed", "closed"].includes(detail.status) && (
              <div className="border-t border-slate-100 pt-4">
                {detail.csat_rating ? (
                  <div className="text-center py-2">
                    <div className="text-sm font-semibold text-slate-700">{t("tickets.youRatedPrefix")} {detail.csat_rating}/5</div>
                    {detail.csat_comment && <div className="text-xs text-slate-500 mt-1">"{detail.csat_comment}"</div>}
                  </div>
                ) : (
                  <div data-testid="feedback-widget">
                    <SectionLabel className="mb-3">{t("tickets.howWasResolution")}</SectionLabel>
                    <SmileyRating value={rating} onChange={setRating} />
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Anything else you'd like to share? (optional)"
                      data-testid="feedback-comment"
                      className="w-full mt-3 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:border-emerald-400"
                      rows={2}
                    />
                    <button
                      onClick={submitFeedback}
                      disabled={submittingFeedback || !rating}
                      data-testid="feedback-submit"
                      className="w-full mt-3 bg-[#0D4721] text-white py-3 rounded-xl text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2"
                    >
                      {submittingFeedback && <Loader2 className="w-4 h-4 animate-spin" />} {t("tickets.submitFeedback")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Sheet>
    </div>
  );
}
